import { runInAction } from "mobx"

import type { IEventBus } from "@/domains/events"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { ILockLookup } from "@/domains/lock/interfaces"
import type { ITokenLookup } from "@/domains/tokens/types"
import { LcAddress } from "@/domains/core"
import { OrchestratedQuery } from "@/domains/library"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type {
  IEmissionsConfig,
  IGaugeApiTransport,
  IGaugeConnectorStrategy,
  IGaugeOnChainTransport,
  IGaugeRepository,
} from "./interfaces"
import type { GaugeApiData } from "./types"
import {
  GaugeAddedToRepositoryEvent,
  GaugeConnectedToClFarmEvent,
  GaugeConnectedToMinimalPoolEvent,
  GaugeCreatedEvent,
  GaugeVotedEvent,
} from "./events"
import { convertApiResponseToGaugeStoreDAO } from "./gauge-converter"
import { GaugeStore } from "./gauge-store"
import { REVENUE_SOURCE_TYPE } from "./interfaces"

export class GaugeOrchestrator {
  private loadGaugeBribesQuery!: OrchestratedQuery<Record<string, GaugeApiData>>
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private gaugeCreatedHandler: ((event: GaugeCreatedEvent) => void) | undefined
  private gaugeVotedHandler: ((event: GaugeVotedEvent) => void) | undefined
  private gaugeAddedHandler: ((event: GaugeAddedToRepositoryEvent) => void) | undefined

  constructor(
    private readonly gaugeRepository: IGaugeRepository,
    private readonly gaugeApiTransport: IGaugeApiTransport,
    private readonly gaugeOnChainTransport: IGaugeOnChainTransport,
    private readonly eventBus: IEventBus,
    private readonly connectorStrategies: IGaugeConnectorStrategy[],
    private readonly emissionsConfig: IEmissionsConfig | undefined,
    private readonly tokenLookup: ITokenLookup,
    private readonly lockLookup: ILockLookup,
  ) {}

  initialize(loadGaugeBribesQuery: OrchestratedQuery<Record<string, GaugeApiData>>) {
    this.loadGaugeBribesQuery = loadGaugeBribesQuery

    // Start the query immediately - gauges are public data and should be available even without wallet connection
    this.loadGaugeBribesQuery.start()

    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Subscribe to gauge creation events (from on-chain watcher)
    this.gaugeCreatedHandler = this.handleGaugeCreated.bind(this)
    this.eventBus.subscribe(GaugeCreatedEvent.TYPE, this.gaugeCreatedHandler)

    // Subscribe to vote events (from on-chain watcher)
    this.gaugeVotedHandler = this.handleGaugeVoted.bind(this)
    this.eventBus.subscribe(GaugeVotedEvent.TYPE, this.gaugeVotedHandler as (event: any) => void)

    // Subscribe to gauge added events to attempt pool connections
    this.gaugeAddedHandler = this.handleGaugeAdded.bind(this)
    this.eventBus.subscribe(GaugeAddedToRepositoryEvent.TYPE, this.gaugeAddedHandler)
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // Note: We do NOT clear gauges on wallet disconnect because:
    // 1. Gauge data is public blockchain data, not user-specific
    // 2. Revenue sources should remain visible even when wallet is disconnected
    // 3. User-specific calculations are handled in VotingGauge, which gracefully handles missing wallet data
    // Keep the query running - gauges are public data and should remain available
    // The query will continue to sync gauge data even without wallet connection
  }

  private async handleWalletConnect(_event: WalletConnectedEvent): Promise<void> {
    // Note: We do NOT clear gauges on wallet connect because:
    // 1. Gauge data is public and doesn't change based on wallet connection
    // 2. The query is already running from initialization
    // 3. User-specific data is calculated in VotingGauge, which will update when wallet connects

    // Ensure the query is running (it should already be running from initialization)
    if (!this.loadGaugeBribesQuery.isRunning) {
      this.loadGaugeBribesQuery.start()
    }
  }

  private async handleGaugeCreated(event: GaugeCreatedEvent): Promise<void> {
    // Create a new gauge store from the event data
    // We'll need to fetch additional data from API or construct minimal data
    // For now, we'll create a minimal gauge and let the API sync fill in details
    const gaugeAddress = new LcAddress(event.gaugeAddress)

    // Check if gauge already exists
    const existing = this.gaugeRepository.find(gaugeAddress)
    if (existing) {
      return
    }

    // Create minimal gauge data from event
    // In a real implementation, we might want to fetch full data immediately
    // For now, we'll create a placeholder that will be updated by the API sync
    const minimalGaugeData: GaugeApiData = {
      address: event.gaugeAddress,
      pair: {
        address: event.poolAddress,
        displayName: "Locating source...",
        displayType: null,
        extendedType: null,
        tags: [],
        tvlUsd: 0,
      },
      token0: {
        address: event.poolAddress, // Placeholder - will be updated by API sync
        symbol: "",
        tags: [],
      },
      token1: {
        address: event.poolAddress, // Placeholder - will be updated by API sync
        symbol: "",
        tags: [],
      },
      bribeAddress: event.bribeAddress,
      apr: "0",
      aprLastEpoch: "0",
      rewardRate: "0",
      totalBribeValueUsd: "0",
      totalSupply: "0",
      votes: "0",
      votesPercent: "0",
      incentives: {},
    }

    // Process the minimal gauge data (incentives will be empty array after transformation)
    this.processGaugeFromAPI(minimalGaugeData)

    // Attempt to connect to pool
    await this.attemptGaugeToPoolConnection(gaugeAddress)
  }

  private async handleGaugeVoted(event: GaugeVotedEvent): Promise<void> {
    // When a vote event is detected, refresh vote data for all gauges
    // Since the Voted event doesn't tell us which specific gauge was voted on,
    // we refresh all gauges to ensure real-time updates
    const gauges = this.gaugeRepository.getAll()

    // Refresh vote data for all gauges in parallel
    const refreshPromises = gauges.map(async (gauge) => {
      const voteData = await this.gaugeOnChainTransport.fetchGaugeVoteData(gauge.address)
      if (voteData) {
        runInAction(() => {
          gauge.updateVoteData(voteData.votes, voteData.votesPercent)
        })
      }
    })

    await Promise.all(refreshPromises)
  }

  private handleGaugeAdded(event: GaugeAddedToRepositoryEvent): void {
    // When a gauge is added, attempt to connect it to a pool
    void this.attemptGaugeToPoolConnection(event.gaugeAddress)
  }

  private async attemptGaugeToPoolConnection(gaugeAddress: LcAddress): Promise<void> {
    const gauge = this.gaugeRepository.find(gaugeAddress)
    if (!gauge) {
      // console.warn(
      //   `[CL-FARM-GAUGE-CONNECTION] ❌ Gauge ${gaugeAddress.toString()} not found in repository when attempting connection`,
      // )
      return
    }

    if (gauge.revenueSource) {
      // Already connected - nothing to do
      return
    }

    // Try each connector strategy
    let foundRevenueSource = false
    for (const strategy of this.connectorStrategies) {
      const revenueSource = strategy.findRevenueSourceByGauge(gaugeAddress)
      if (revenueSource) {
        foundRevenueSource = true
        runInAction(() => {
          gauge.connectToRevenueSource(revenueSource)
        })

        // Publish the appropriate event based on the revenue source type
        if (strategy.revenueSourceType === REVENUE_SOURCE_TYPE.MINIMAL_POOL) {
          this.eventBus.publish(new GaugeConnectedToMinimalPoolEvent(gaugeAddress, revenueSource.address))
        } else if (strategy.revenueSourceType === REVENUE_SOURCE_TYPE.CL_FARM) {
          this.eventBus.publish(new GaugeConnectedToClFarmEvent(gaugeAddress, revenueSource.address))
        }

        break
      }
    }

    // Only log if we couldn't find a revenue source for this gauge
    if (!foundRevenueSource) {
      // console.warn(
      //   `[CL-FARM-GAUGE-CONNECTION] ❌ Gauge ${gaugeAddress.toString()} (revenueSourceAddress: ${gauge.revenueSourceAddress.toString()}) - No revenue source found after trying ${this.connectorStrategies.length} connector strategies`,
      // )
    }
  }

  get query(): OrchestratedQuery<Record<string, GaugeApiData>> {
    return this.loadGaugeBribesQuery
  }

  /**
   * Load gauge bribes from API (triggered by OrchestratedQuery)
   */
  async onApiLoadGaugeBribesTrigger(
    _query: IOrchestratedQuerySender<Record<string, GaugeApiData>>,
  ): Promise<Record<string, GaugeApiData>> {
    return await this.gaugeApiTransport.loadGaugeBribes()
  }

  /**
   * Callback for when gauge bribes query succeeds
   */
  onGaugeBribesQuerySuccess(
    _sender: IOrchestratedQuerySender<Record<string, GaugeApiData>>,
    data: Record<string, GaugeApiData>,
  ): void {
    try {
      runInAction(() => {
        // Process all gauges from API response
        const gauges = Object.values(data)
        for (const gaugeData of gauges) {
          this.processGaugeFromAPI(gaugeData)
        }

        // Delta diff: Remove gauges from repository that are no longer in the API response
        const apiGaugeAddresses = new Set(gauges.map((g) => g.address))
        const existingGauges = this.gaugeRepository.getAll()
        for (const gauge of existingGauges) {
          if (!apiGaugeAddresses.has(gauge.address.toString())) {
            // Note: We might not want to remove gauges that were created on-chain
            // but not yet in API. For now, we'll keep them.
          }
        }
      })

      // After all gauges have been processed, check for any revenue sources that couldn't find their gauges
      // This is done via events - revenue sources subscribe to gauge added events and will attempt connection
      // The warning logic is in the revenue source orchestrators (ClFarmOrchestrator, MinimalPoolOrchestrator)
    } catch (error) {
      console.error("Error processing gauge data:", error)
    }
  }

  /**
   * Callback for when gauge bribes query fails
   */
  onGaugeBribesQueryFailed(_sender: IOrchestratedQuerySender<Record<string, GaugeApiData>>, error: Error): void {
    console.error("Failed to load gauge bribes", error)
  }

  /**
   * Process gauge data from API and add to repository
   */
  private processGaugeFromAPI(apiData: GaugeApiData): void {
    const gaugeAddress = new LcAddress(apiData.address)
    const found = this.gaugeRepository.find(gaugeAddress)

    if (found) {
      // Update existing gauge
      const gaugeStoreDAO = convertApiResponseToGaugeStoreDAO(apiData, this.tokenLookup)
      found.updateStaticData(gaugeStoreDAO)
      found.updateIncentives(gaugeStoreDAO.incentives)
      return
    }

    // Convert API data to store DAO
    const gaugeStoreDAO = convertApiResponseToGaugeStoreDAO(apiData, this.tokenLookup)

    // Create new gauge store
    const gauge = new GaugeStore(gaugeStoreDAO)

    // Update incentives (convert DAO array to Array of IncentiveStore instances)
    gauge.updateIncentives(gaugeStoreDAO.incentives)

    // Add to repository
    this.gaugeRepository.add(gauge)

    // Attempt to connect to revenue source
    void this.attemptGaugeToPoolConnection(gaugeAddress)
  }
}
